// ── Display currency ──
// The header's currency selector picks a ship-to country; everything the
// catalog shows is then converted from its stored USD price into that
// country's currency at a rate fetched when the selection is made.
//
// The selection is deliberately read SYNCHRONOUSLY from localStorage at module
// load and changing it reloads the page. That keeps conversion a pure function
// for every price on screen — no context, no async render states, and no way
// for half the page to show one currency while the rest shows another.
import COUNTRY_CURRENCIES from "../data/countryCurrencies.json";

const STORAGE_KEY = "display-currency";

export const DEFAULT_SELECTION = Object.freeze({
  country: "US",
  currency: "USD",
  rate: 1,
  fetchedAt: 0,
});

const safeStorage = typeof window !== "undefined" ? window.localStorage : null;

function readSelection() {
  if (!safeStorage) return DEFAULT_SELECTION;
  try {
    const raw = safeStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SELECTION;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_SELECTION;
    const { country, currency, rate } = parsed;
    // A malformed or non-positive rate would silently zero out every price on
    // the site, so fall back to USD rather than trusting it.
    if (
      typeof country !== "string" ||
      typeof currency !== "string" ||
      typeof rate !== "number" ||
      !Number.isFinite(rate) ||
      rate <= 0
    ) {
      return DEFAULT_SELECTION;
    }
    if (!COUNTRY_CURRENCIES[country]) return DEFAULT_SELECTION;
    return { country, currency, rate, fetchedAt: parsed.fetchedAt || 0 };
  } catch {
    // Private mode / blocked storage / bad JSON all land here.
    return DEFAULT_SELECTION;
  }
}

// Frozen at module load on purpose — see the note at the top of the file.
let selection = readSelection();

export function getDisplayCurrency() {
  return selection;
}

export function persistDisplayCurrency(next) {
  selection = next;
  if (!safeStorage) return;
  try {
    safeStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota or private mode — the selection just won't survive the reload */
  }
}

export function resetDisplayCurrency() {
  selection = DEFAULT_SELECTION;
  if (!safeStorage) return;
  try {
    safeStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function currencyForCountry(countryCode) {
  return COUNTRY_CURRENCIES[countryCode] || null;
}

/** USD amount -> the selected display currency. Identity while USD is selected. */
export function convertFromUsd(usdAmount) {
  if (typeof usdAmount !== "number" || !Number.isFinite(usdAmount)) return usdAmount;
  if (selection.currency === "USD") return usdAmount;
  return usdAmount * selection.rate;
}

/** True when prices on screen are NOT the USD figure the buyer is charged. */
export function isConverted() {
  return selection.currency !== "USD";
}

// ── Display helpers ────────────────────────────────────────────────────────
// Country names and currency symbols come from Intl rather than another data
// file, so they stay correct and localised without anything to maintain.

let regionNames = null;
export function countryName(code) {
  if (regionNames === null) {
    try {
      regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      regionNames = false;
    }
  }
  if (!regionNames) return code;
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}

const symbolCache = new Map();
export function currencySymbol(currency) {
  if (symbolCache.has(currency)) return symbolCache.get(currency);
  let symbol = currency;
  try {
    const part = new Intl.NumberFormat("en", { style: "currency", currency })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    if (part?.value) symbol = part.value;
  } catch {
    /* keep the code as the symbol */
  }
  symbolCache.set(currency, symbol);
  return symbol;
}

// The currencies the selector offers, one representative country each.
//
// The data behind this holds 237 countries and 150 currencies, which made the
// picker a gazetteer — 36 separate rows all priced in euros, and a long tail
// of currencies with no live rate behind them. This is the majors: what
// anyone shopping here is realistically paying in.
//
// One row per CURRENCY, not per country: the country is only ever used to
// derive the currency and to label the control, so listing every euro country
// added rows without adding choices. EU ("European Union" to Intl) stands for
// the eurozone rather than an arbitrary member state.
//
// Adding one back is a line here — any code already in countryCurrencies.json
// works, and a selection saved earlier under a country NOT on this list still
// resolves, it simply is not listed.
const OFFERED_COUNTRIES = [
  "US", // USD
  "EU", // EUR
  "GB", // GBP
  "JP", // JPY
  "CN", // CNY
  "KR", // KRW
  "CA", // CAD
  "AU", // AUD
  "CH", // CHF
  "HK", // HKD
  "SG", // SGD
  "TW", // TWD
  "SE", // SEK
  "NO", // NOK
  "DK", // DKK
  "NZ", // NZD
  "MY", // MYR (the owner, 2026-09-30)
  "PH", // PHP
  "RU", // RUB
];

/** The ship-to countries on offer, sorted by name, with currency and symbol. */
let optionsCache = null;
export function currencyOptions() {
  if (optionsCache) return optionsCache;
  optionsCache = OFFERED_COUNTRIES
    .filter((country) => COUNTRY_CURRENCIES[country])
    .map((country) => ({
      country,
      currency: COUNTRY_CURRENCIES[country],
      name: countryName(country),
      symbol: currencySymbol(COUNTRY_CURRENCIES[country]),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return optionsCache;
}

/** Short label for the header control, e.g. "US (USD $)". */
export function selectionLabel(sel = selection) {
  return `${sel.country} (${sel.currency} ${currencySymbol(sel.currency)})`;
}
